import {tokens} from '@pleo-io/telescope'
import React from 'react'
import styled from 'styled-components'

import Image from '@helios/shared/components/image'
import RichText from '@helios/shared/components/rich-text'
import {
    SimpleSignupForm,
    Props as SimpleSignupFormProps,
} from '@helios/shared/components/simple-signup-form'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {Section} from '@helios/shared/styles/grid'
import {Text, TextProps} from '@helios/shared/styles/typography'
import {colors, radii} from '@helios/shared/theme'
import ImageSource from '@helios/shared/types/image'
import mq from '@helios/shared/utils/media-query'

export interface Props extends Omit<SimpleSignupFormProps, 'withBorder'> {
    color?: keyof typeof colors.background
    title: string
    subtitle: string
    image?: ImageSource | null
    titleSize?: TextProps['size']
    recaptchaElement?: JSX.Element
    showLegalText?: boolean
    ariaLabel?: string
    dataTestId?: string
}

const SignupBanner = ({
    color = 'pink',
    title,
    subtitle,
    image,
    titleSize = 'xl',
    recaptchaElement,
    showLegalText,
    ariaLabel,
    dataTestId,
    ...simpleSignupFormProps
}: Props) => {
    const globalStrings = useGlobalStrings()
    const label = ariaLabel ?? globalStrings.yourEmailLabel

    return (
        <StyledSection $color={color} id="signupform" data-testid={`${dataTestId}-section-wrapper`}>
            <Wrapper $color={color}>
                <div>
                    <Header>
                        <Text size={titleSize} as="h3">
                            {title}
                        </Text>

                        <Text>{subtitle}</Text>
                    </Header>

                    <FormWrapper>
                        <SimpleSignupForm
                            {...simpleSignupFormProps}
                            ariaLabel={label}
                            withBorder={color === 'default'}
                            dataTestId={dataTestId}
                        />

                        {showLegalText && recaptchaElement && (
                            <div>
                                {recaptchaElement}

                                <RichText
                                    bgColor="black"
                                    size="xxs"
                                    font="secondary"
                                    color="muted"
                                    content={globalStrings.recaptcha_message}
                                />
                            </div>
                        )}
                    </FormWrapper>
                </div>

                {image && <Image image={image} width={320} />}
            </Wrapper>
        </StyledSection>
    )
}

type ColorProps = {
    $color: NonNullable<Props['color']>
}

const StyledSection = styled(Section)<ColorProps>`
    ${mq.max('md')} {
        background: ${(props) => colors.background[props.$color]};
    }
`

const Wrapper = styled.div<ColorProps>`
    display: grid;
    background: ${(props) => colors.background[props.$color]};
    border-radius: ${radii.arc20};
    grid-template-columns: 1fr;
    row-gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        grid-template-columns: 1fr 0.6fr;
        column-gap: ${tokens.spacing40};
    }

    ${mq.min('md')} {
        padding: 80px;
    }

    ${mq.min('xl')} {
        column-gap: 100px;
    }

    img {
        justify-self: center;
        align-self: center;
        width: 100%;
        max-width: 200px;
        grid-row: 1;

        ${mq.min('sm')} {
            grid-row: auto;
        }

        ${mq.min('md')} {
            max-width: 320px;
        }
    }
`

const Header = styled.div`
    display: grid;
    row-gap: ${tokens.spacing10};
    margin-bottom: ${tokens.spacing32};

    ${mq.min('md')} {
        margin-bottom: ${tokens.spacing40};
    }
`

const FormWrapper = styled.div`
    display: grid;
    row-gap: ${tokens.spacing20};
`

export default SignupBanner
